import { computed } from 'vue'

import constrain from '@nicolaudie-group/ng-ui-kit/helpers/constrain'
import useRef from '@nicolaudie-group/ng-ui-kit/composables/useRef'

export function computeSize(start, end) {
  const delta = end + 1 - start
  return constrain(1, delta, 256)
}

export function getSize({
  start,
  end,
  size,
} = {}) {
  if (typeof size === 'undefined') {
    // If no size was provided
    if (typeof start !== 'undefined' && typeof end !== 'undefined') {
      // But there is a start and end value
      // Compute the size from the start and end value
      return computeSize(start, end)
    }
    // If there is also no start value or no end value
    // Throw an error
    throw new Error('Not enough parameters to infer size of DMXRange')
  }
  return size
}

class DMXRange {
  /**
   * Symbol used to store and access the "sizes" ref on the DMXRange
   */
  static sizes = Symbol('DMX Ranges sizes ref')

  /**
   * Symbol used to store and access the "ranges" array on the DMXRange
   */
  static ranges = Symbol('DMX Ranges')

  constructor(sizes, ranges, state) {
    // Use symbols to store reference to the "sizes" ref,
    // so that it's not picked up by vue, but can be used in the methods
    this[DMXRange.sizes] = sizes
    // Use symbols to store reference to the "ranges" array,
    // so that it's not picked up by vue, but can be used in the methods
    this[DMXRange.ranges] = ranges

    Object.assign(this, state)
  }

  // Use a getter to retrieve the DMX Ranges array,
  // so that it's not picked up by vue and made recursively reactive
  get ranges() {
    return this[DMXRange.ranges]
  }

  // Use a getter to retrieve the "previous" DMX Range,
  // so that it's not picked up by vue and made recursively reactive
  get previous() {
    return this.ranges[this.index - 1]
  }

  // Use a getter to retrieve the "next" DMX Range,
  // so that it's not picked up by vue and made recursively reactive
  get next() {
    return this.ranges[this.index + 1]
  }

  // Use a getter to retrieve the value of the "sizes" ref
  get sizes() {
    // It's okay to return the value directly here,
    // since it's an array it'll be a reference anyways
    return this[DMXRange.sizes].value
  }

  setSize(size) {
    // It's okay to mutate a size in the array directly,
    // since it's an array it keeps the reference
    this.sizes[this.index] = size
  }

  /**
   * DO NOT USE
   * For internal use only
   * @param {number} amount - Amount to shrink by
   * @param {boolean} control - Shrink only if this is true
   * @returns Amount shrunk by
   */
  shrink(amount, control = true) {
    if (control) {
      const shrinkBy = constrain(0, amount, this.size - 1)
      this.setSize(this.size - shrinkBy)
      return shrinkBy
    }
    return 0
  }

  /**
   * Shrink at the start, so grows the previous range
   * @param {number} amount - Amount to shrink by
   * @param {boolean} recursive - DO  NOT USE - For internal use only
   */
  shrinkStart(amount, recursive = false) {
    const shrinked = this.shrink(amount, this.startCanShrink)
    if (recursive) {
      // if we're shrinking many ranges (i.e to free space for a growing range)
      const leftToShrink = amount - shrinked
      if (leftToShrink > 0 && this.next) {
        // shrink the next range (and the following ranges if needed) by the amount left to shrink
        this.next.shrinkStart(leftToShrink, true)
      }
    } else if (this.previous) {
      // if we're only shrinking a single DMX range, grow the previous range
      this.previous.growEnd(shrinked, true)
    }
  }

  /**
   * Shrink at the end, so grows the next range
   * @param {number} amount - Amount to shrink by
   * @param {boolean} recursive - DO  NOT USE - For internal use only
   */
  shrinkEnd(amount, recursive = false) {
    const shrinked = this.shrink(amount, this.endCanShrink)
    if (recursive) {
      // if we're shrinking many ranges (i.e to free space for a growing range)
      const leftToShrink = amount - shrinked
      if (leftToShrink > 0 && this.previous) {
        // shrink the prev range (and the following ranges if needed) by the amount left to shrink
        this.previous.shrinkEnd(leftToShrink, true)
      }
    } else if (this.next) {
      // if we're only shrinking a single DMX range, grow the next range
      this.next.growStart(shrinked, true)
    }
  }

  /**
   * Grow at the start, so shrinks the previous range
   * @param {number} amount - Amount to grow by
   * @param {boolean} recursive - DO  NOT USE - For internal use only
   */
  growStart(amount, recursive = false) {
    if (recursive) {
      this.setSize(this.size + amount)
    } else if (this.previous) {
      const constrained = constrain(0, amount, this.spaceBefore)
      this.setSize(this.size + constrained)
      this.previous.shrinkEnd(constrained, true)
    }
  }

  /**
   * Grow at the end, so shrinks the next range
   * @param {number} amount - Amount to grow by
   * @param {boolean} recursive - DO  NOT USE - For internal use only
   */
  growEnd(amount, recursive = false) {
    if (recursive) {
      this.setSize(this.size + amount)
    } else if (this.next) {
      const constrained = constrain(0, amount, this.spaceAfter)
      this.setSize(this.size + constrained)
      this.next.shrinkStart(constrained, true)
    }
  }

  /**
   * Move the start value of the range by the provided delta
   * @param {number} delta - Direction and how much to move the start value
   * If the delta is negative, the start value will be moved towards 0.
   * If the delta is positive, the start value will be moved towards 255.
   */
  moveStart(delta) {
    const constrained = constrain(-this.spaceBefore, delta, this.size - 1)
    if (constrained > 0) {
      this.shrinkStart(constrained)
    } else if (constrained < 0) {
      this.growStart(Math.abs(constrained))
    }
  }

  /**
   * Move the end value of the range by the provided delta
   * @param {number} delta - Direction and how much to move the end value
   * If the delta is negative, the end value will be moved towards 0.
   * If the delta is positive, the end value will be moved towards 255.
   */
  moveEnd(delta) {
    const constrained = constrain(-(this.size - 1), delta, this.spaceAfter)
    if (constrained > 0) {
      this.growEnd(constrained)
    } else if (constrained < 0) {
      this.shrinkEnd(Math.abs(constrained))
    }
  }

  /**
   * Move the start value towards the provided start value as much as possible
   * @param {number} value - Value aimed for the start value
   */
  moveStartTo(value) {
    const constrained = constrain(this.minLimit, value, this.maxLimit)
    const delta = constrained - this.start
    this.moveStart(delta)
  }

  /**
   * Move the end value towards the provided end value as much as possible
   * @param {number} start - Value aimed for the end value
   */
  moveEndTo(value) {
    const constrained = constrain(this.minLimit, value, this.maxLimit)
    const delta = constrained - this.end
    this.moveEnd(delta)
  }
}

function useDmxRanges(initialSizes) {
  const sizes = useRef(initialSizes)

  const ranges = computed(() => {
    const result = []

    let nextStart = 0 // Temporary

    for (let i = 0; i < sizes.value.length; i += 1) {
      const size = sizes.value[i]

      // Compute "start"
      const start = nextStart
      nextStart = start + size // Temporary

      // Compute "end"
      const end = start + size - 1

      // Compute "startCanShrink" & "endCanShrink"
      const hasPrevious = typeof sizes.value[i - 1] !== 'undefined' // Temporary
      const hasNext = typeof sizes.value[i + 1] !== 'undefined' // Temporary
      const bigEnough = size > 1 // Temporary
      const startCanShrink = hasPrevious && bigEnough
      const endCanShrink = hasNext && bigEnough

      // Compute "canShrink"
      const canShrink = startCanShrink || endCanShrink

      // Compute "rangesBefore" & "rangesAfter"
      const rangesBefore = i
      const rangesAfter = sizes.value.length - 1 - i

      // Compute "minLimit" & "maxLimit"
      const minLimit = rangesBefore
      const maxLimit = 255 - rangesAfter

      // Compute "spaceBefore" & "spaceAfter"
      const spaceBefore = start - minLimit
      const spaceAfter = maxLimit - end

      // Compute "startCanGrow" & "endCanGrow"
      const startCanGrow = !!spaceBefore
      const endCanGrow = !!spaceAfter

      // Compute "startCanChange" & "endCanChange"
      const startCanChange = startCanShrink || startCanGrow
      const endCanChange = endCanShrink || endCanGrow

      const range = new DMXRange(sizes, result, {
        index: i,
        size,
        start,
        end,
        startCanShrink,
        endCanShrink,
        canShrink,
        rangesBefore,
        rangesAfter,
        minLimit,
        maxLimit,
        spaceBefore,
        spaceAfter,
        startCanGrow,
        endCanGrow,
        startCanChange,
        endCanChange,
      })

      result.push(range)
    }

    return result
  })

  return ranges
}

export default useDmxRanges
